import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, Loader2, Lock, Upload, Trash2, Pencil, Check, X, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, API, aud } from "@/lib/api";
import Seo from "@/components/Seo";

const fmtSize = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const PartRow = ({ productId, file, fileKey, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(file.label);
  const [busy, setBusy] = useState(false);

  const rename = async () => {
    if (!label.trim() || label.trim() === file.label) return setEditing(false);
    setBusy(true);
    try {
      await api.patch(`/preview/${fileKey}/${productId}/${file.name}`, { label: label.trim() });
      toast.success("Part renamed");
      onChanged();
    } catch {
      toast.error("Could not rename this part");
    } finally {
      setBusy(false);
      setEditing(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove "${file.label}" from this toolkit?`)) return;
    setBusy(true);
    try {
      await api.delete(`/preview/${fileKey}/${productId}/${file.name}`);
      toast.success("Part removed");
      onChanged();
    } catch {
      toast.error("Could not remove this part");
      setBusy(false);
    }
  };

  return (
    <li className="flex items-center justify-between gap-3 px-6 py-3.5" data-testid={`part-row-${productId}-${file.name}`}>
      <div className="flex items-center gap-3 min-w-0">
        <FileText className="w-4 h-4 text-teal/50 shrink-0" />
        {editing ? (
          <span className="flex items-center gap-2">
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && rename()}
              className="text-sm border border-teal px-2 py-1 bg-ivory focus:outline-none"
              data-testid={`part-rename-input-${productId}-${file.name}`}
              autoFocus
            />
            <button onClick={rename} disabled={busy} className="text-teal" data-testid={`part-rename-save-${productId}-${file.name}`} aria-label="Save name">
              <Check className="w-4 h-4" />
            </button>
            <button onClick={() => { setEditing(false); setLabel(file.label); }} className="text-charcoal/50" aria-label="Cancel">
              <X className="w-4 h-4" />
            </button>
          </span>
        ) : (
          <span className="text-sm text-charcoal/85 truncate">
            {file.label}
            <span className="ml-2 text-xs text-charcoal/40 font-mono">{fmtSize(file.size)}</span>
          </span>
        )}
      </div>
      <span className="flex items-center gap-1 shrink-0">
        <a
          href={`${API}${file.url.replace("/api", "")}`}
          target="_blank"
          rel="noreferrer"
          className="p-2 text-teal hover:bg-sand-light transition-colors"
          data-testid={`part-open-${productId}-${file.name}`}
          aria-label="Open PDF"
        >
          <Download className="w-4 h-4" />
        </a>
        <button
          onClick={() => setEditing(true)}
          className="p-2 text-charcoal/50 hover:text-teal hover:bg-sand-light transition-colors"
          data-testid={`part-rename-${productId}-${file.name}`}
          aria-label="Rename part"
        >
          <Pencil className="w-4 h-4" />
        </button>
        <button
          onClick={remove}
          disabled={busy}
          className="p-2 text-charcoal/50 hover:text-red-700 hover:bg-red-50 transition-colors"
          data-testid={`part-delete-${productId}-${file.name}`}
          aria-label="Delete part"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </span>
    </li>
  );
};

const UploadForm = ({ productId, fileKey, onChanged }) => {
  const [label, setLabel] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const upload = async () => {
    if (!file) return toast.error("Choose a PDF first");
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("label", label);
      await api.post(`/preview/${fileKey}/upload/${productId}`, form);
      toast.success(`"${label.trim() || file.name}" added — toolkit updated`);
      setLabel("");
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-6 py-4 bg-sand-light/60 border-t border-cloud flex flex-col sm:flex-row gap-3 sm:items-center">
      <input
        type="text"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Part name (e.g. Education Guide)"
        className="flex-1 text-sm border border-cloud bg-white px-3 py-2.5 focus:outline-none focus:border-teal"
        data-testid={`upload-label-${productId}`}
      />
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        className="text-xs text-charcoal/70 file:mr-3 file:bg-white file:border file:border-cloud file:px-3 file:py-2 file:text-xs file:font-mono file:uppercase file:tracking-[0.12em] file:text-teal hover:file:border-teal"
        data-testid={`upload-file-${productId}`}
      />
      <button
        onClick={upload}
        disabled={busy || !file}
        className="inline-flex items-center justify-center gap-2 bg-teal text-ivory text-xs font-mono uppercase tracking-[0.14em] px-5 py-2.5 hover:bg-teal-dark transition-colors disabled:opacity-50"
        data-testid={`upload-submit-${productId}`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {busy ? "Uploading…" : "Add PDF"}
      </button>
    </div>
  );
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short" }) + " " +
    d.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });
};

const InsightsPanel = ({ fileKey }) => {
  const [sessions, setSessions] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [thread, setThread] = useState(null);

  useEffect(() => {
    api
      .get(`/preview/${fileKey}/insights/chats`)
      .then(({ data }) => setSessions(data.sessions))
      .catch(() => setSessions([]));
  }, [fileKey]);

  const openThread = async (sid) => {
    if (openId === sid) return setOpenId(null);
    setOpenId(sid);
    setThread(null);
    try {
      const { data } = await api.get(`/preview/${fileKey}/insights/chats/${sid}`);
      setThread(data);
    } catch {
      setThread({ messages: [] });
    }
  };

  if (!sessions) {
    return (
      <div className="py-16 flex justify-center">
        <Loader2 className="w-7 h-7 text-teal animate-spin" />
      </div>
    );
  }

  const leads = sessions.filter((s) => s.has_lead).length;

  return (
    <div data-testid="insights-panel">
      <p className="text-sm text-charcoal/70 max-w-2xl leading-relaxed">
        What visitors are asking the AI planning assistant — use it to spot demand for new
        toolkits and guides. Private to this link.
      </p>
      <p className="mt-4 text-xs font-mono uppercase tracking-[0.18em] text-charcoal/50" data-testid="insights-summary">
        {sessions.length} conversation{sessions.length !== 1 ? "s" : ""} · {leads} lead{leads !== 1 ? "s" : ""} captured
      </p>
      {sessions.length === 0 ? (
        <p className="mt-10 text-sm text-charcoal/60 bg-white border border-cloud p-7" data-testid="insights-empty">
          No conversations yet. Once visitors start chatting with the assistant, their questions
          will appear here.
        </p>
      ) : (
        <div className="mt-8 space-y-3">
          {sessions.map((s) => (
            <div key={s.session_id} className="bg-white border border-cloud" data-testid={`insight-${s.session_id}`}>
              <button
                onClick={() => openThread(s.session_id)}
                className="w-full px-6 py-4 flex items-start justify-between gap-4 text-left hover:bg-sand-light/40 transition-colors"
                data-testid={`insight-open-${s.session_id}`}
              >
                <div className="min-w-0">
                  <p className="text-sm text-charcoal/85 leading-relaxed line-clamp-2">
                    {s.first_question || "(no question recorded)"}
                  </p>
                  <p className="mt-1.5 text-xs font-mono text-charcoal/50">
                    {fmtDate(s.updated_at)} · {s.message_count} message{s.message_count !== 1 ? "s" : ""}
                  </p>
                </div>
                {s.has_lead && (
                  <span className="shrink-0 text-[10px] font-mono uppercase tracking-[0.12em] bg-teal text-ivory px-2.5 py-1">
                    Left details
                  </span>
                )}
              </button>
              {openId === s.session_id && (
                <div className="border-t border-cloud px-6 py-5 space-y-3 bg-ivory" data-testid={`insight-thread-${s.session_id}`}>
                  {!thread ? (
                    <Loader2 className="w-5 h-5 text-teal animate-spin" />
                  ) : (
                    (thread.messages || []).map((m, i) => (
                      <div key={i} className={`text-sm leading-relaxed max-w-2xl ${m.role === "user" ? "text-teal font-medium" : "text-charcoal/75 pl-5 border-l-2 border-sand"}`}>
                        <span className="block text-[10px] font-mono uppercase tracking-[0.12em] text-charcoal/45 mb-0.5">
                          {m.role === "user" ? "Visitor" : "Assistant"}
                        </span>
                        {m.text}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Library = () => {
  const { key } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState("products");

  const load = () =>
    api
      .get(`/preview/${key}`)
      .then(({ data }) => setData(data))
      .catch(() => setError(true));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (error) {
    return (
      <div className="py-32 text-center" data-testid="library-denied">
        <Lock className="w-10 h-10 text-charcoal/30 mx-auto" />
        <h1 className="mt-4 font-serif text-2xl text-teal">This library link is not valid</h1>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="py-32 flex justify-center">
        <Loader2 className="w-8 h-8 text-teal animate-spin" />
      </div>
    );
  }

  const onSale = data.products.filter((p) => p.available).length;

  return (
    <div className="py-16 lg:py-20" data-testid="owner-library">
      <Seo title="Owner Studio" noindex />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-mono uppercase tracking-[0.24em] text-teal">Owner Studio</p>
        <h1 className="mt-3 font-serif text-3xl sm:text-4xl text-teal tracking-tight">
          {tab === "products" ? "Toolkit Products" : "Chat Insights"}
        </h1>
        <div className="mt-6 flex gap-2 border-b border-cloud" data-testid="owner-tabs">
          {[
            ["products", "Toolkit Products"],
            ["insights", "Chat Insights"],
          ].map(([t, label]) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`owner-tab-${t}`}
              className={`px-5 py-3 text-xs font-mono uppercase tracking-[0.14em] transition-colors border-b-2 -mb-px ${
                tab === t ? "border-teal text-teal" : "border-transparent text-charcoal/50 hover:text-teal"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {tab === "insights" ? (
          <div className="mt-8">
            <InsightsPanel fileKey={key} />
          </div>
        ) : (
          <>
        <p className="mt-3 text-sm text-charcoal/70 max-w-2xl leading-relaxed">
          Upload your finished PDFs here — as many parts per toolkit as you like, named however you
          like. A toolkit goes on sale automatically the moment it has at least one PDF, and comes
          off sale if you remove them all. This page is private — only people with this exact link
          can see it.
        </p>
        <p className="mt-4 text-xs font-mono uppercase tracking-[0.18em] text-charcoal/50" data-testid="library-status-summary">
          {onSale} of {data.products.length} toolkits on sale
        </p>
        <div className="mt-10 space-y-6">
          {data.products.map((p) => (
            <div key={p.product_id} className="bg-white border border-cloud" data-testid={`library-${p.product_id}`}>
              <div className="px-6 py-4 border-b border-cloud flex items-center justify-between gap-4">
                <h2 className="font-serif text-lg text-teal">{p.title}</h2>
                <span className="flex items-center gap-3 shrink-0">
                  <span className="text-sm text-charcoal/50 font-mono hidden sm:inline">{aud(p.price)}</span>
                  <span
                    className={`text-[11px] font-mono uppercase tracking-[0.14em] px-3 py-1.5 ${
                      p.available ? "bg-teal text-ivory" : "bg-sand-light text-charcoal/60"
                    }`}
                    data-testid={`library-status-${p.product_id}`}
                  >
                    {p.available ? `On sale · ${p.files.length} part${p.files.length > 1 ? "s" : ""}` : "Not on sale"}
                  </span>
                </span>
              </div>
              {p.files.length > 0 && (
                <ul className="divide-y divide-cloud">
                  {p.files.map((f) => (
                    <PartRow key={f.name} productId={p.product_id} file={f} fileKey={key} onChanged={load} />
                  ))}
                </ul>
              )}
              <UploadForm productId={p.product_id} fileKey={key} onChanged={load} />
            </div>
          ))}
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Library;
